import { selectOptionsForLanguage, type TranslatableOption } from "./option-translation-service";

export type CleaningSupplyOption = TranslatableOption & {
  typeId: number;
  name: string;
  language?: string | null;
  description?: string | null;
  typeCode?: string | null;
  type_code?: string | null;
  typeCategory?: string | null;
  type_category?: string | null;
  isCleaningSupply?: boolean;
  is_cleaning_supply?: boolean;
};

export type SplitLaundryOptions<T> = {
  cleaningServices: T[];
  laundry: T[];
};

const CLEANING_SUPPLIES_CATEGORY = "cleaning_supplies";

const normalizeCategory = (value?: string | null): string => {
  return (value ?? "").trim().toLowerCase();
};

/**
 * Cleaning supplies are identified exclusively by API-owned markers. Do not
 * infer them from names, option type ids or type codes: regular services such
 * as daily cleaning may contain the same words or use a cleaning icon.
 */
export function isCleaningSupplyOption(option: CleaningSupplyOption): boolean {
  return option.isCleaningSupply === true
    || option.is_cleaning_supply === true
    || normalizeCategory(option.typeCategory) === CLEANING_SUPPLIES_CATEGORY
    || normalizeCategory(option.type_category) === CLEANING_SUPPLIES_CATEGORY;
}

export function splitLaundryOptions<T extends CleaningSupplyOption>(options: T[]): SplitLaundryOptions<T> {
  const cleaningServices: T[] = [];
  const laundry: T[] = [];

  for (const option of options) {
    (isCleaningSupplyOption(option) ? cleaningServices : laundry).push(option);
  }

  return { cleaningServices, laundry };
}

export function excludeCleaningSupplyOptions<T extends CleaningSupplyOption>(options: T[]): T[] {
  return options.filter((option: T) => !isCleaningSupplyOption(option));
}

export function filterCleaningSupplyOptions<T extends CleaningSupplyOption>(options: T[], language: string): T[] {
  return selectOptionsForLanguage(splitLaundryOptions(options).cleaningServices, language);
}

/** The icon is presentational only and never participates in classification. */
export function cleaningSupplyIcon(option: CleaningSupplyOption): string {
  const code = (option.typeCode ?? option.type_code ?? "").toLowerCase();

  if (code.includes("vacuum")) return "mdi-vacuum";
  if (code.includes("bucket") || code.includes("mop")) return "mdi-bucket";
  if (code.includes("broom") || code.includes("brush") || code.includes("squeegee")) return "mdi-broom";

  return "mdi-spray-bottle";
}
